/**
 * backOrigin — IDI-157: "Back" returns to where the user came from.
 *
 * List surfaces that open a quote or job pass an origin in router location
 * state:
 *
 *   navigate(route, { state: originState({ from: `/customers/${id}`, fromTab: 'jobs', label: 'Back to Acme' }) })
 *
 * Detail pages read it back with resolveBack(location.state, fallback) and get
 * the Back href, the state to hand the origin page (so CustomerDetail can
 * reopen the tab the user left), and the label. With no in-app origin, the
 * section default is used ("Back to Quotes" / "Back to Jobs" — Adrian 10-20).
 */

/** Build the origin state a list row passes when it navigates to a detail page. */
export function originState({ from, fromTab, label } = {}) {
  const state = { from };
  if (fromTab) state.fromTab = fromTab;
  if (label) state.label = label;
  return state;
}

/** True when `from` is an in-app absolute path (never an external URL). */
function isInAppPath(from) {
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//');
}

/**
 * Resolve the Back target.
 * @param {object|null|undefined} locationState - router location.state
 * @param {{href: string, label: string}} fallback - the section default
 * @returns {{href: string, state: object|undefined, label: string}}
 */
export function resolveBack(locationState, fallback) {
  const from = locationState && locationState.from;
  if (!isInAppPath(from)) {
    return { href: fallback.href, state: undefined, label: fallback.label };
  }
  const fromTab = locationState.fromTab;
  return {
    href: from,
    state: fromTab ? { tab: fromTab } : undefined,
    label: locationState.label || fallback.label,
  };
}

/**
 * The origin state to carry forward when a page hands the user on to another
 * detail page (e.g. the converted-quote guard redirecting to the job), so Back
 * on the next page still returns to the original list. Returns undefined when
 * there is no in-app origin.
 */
export function carryOrigin(locationState) {
  if (!locationState || !isInAppPath(locationState.from)) return undefined;
  return originState(locationState);
}
